import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField, TextField, TextAreaField, Toggle, normalizeOptions } from './fields';
import EditHistoryLink from './EditHistoryLink';
import ScacLocationMapEditDrawer from './ScacLocationMapEditDrawer';
import type { CustomDrawerProps } from './TablemaintPage';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import { findInvalidEmails } from '../../utils/email';
import { ScacOption, getScacs } from '../../services/scacs.service';
import { ScacLocationMapRow, getRegions, getScacLocationMap } from '../../services/scaclocationmap.service';

/**
 * Bespoke edit/create drawer for SCAC Info (cms.scacinfo) — per (SCAC, region) contact +
 * rail-billing reference info. Identity (SCAC / SS Line / Location / Website / Deleted) + the
 * Rail Port / CY Locations subtable sit at the top; Notes at the bottom; the many contact
 * groups sit in ONE collapsible "Contact & Service Details" section in between (expanded by
 * default) so the whole block can be tucked away when you only need the identity/locations.
 *
 * SCAC + Location are create-only (locked after the first save). Only SCAC is required.
 * ssline is readonly (the active scacprofiles name). The rpcylocations subtable
 * (cms.scaclocationmap) can only be added to once the record exists (its rows key off
 * scac + location, which aren't fixed until create).
 */
const TABLE = 'scacinfo';

const s = (v: any): string => (v == null ? '' : String(v));
const yn = (v: any): 'Y' | 'N' => (s(v).toUpperCase() === 'Y' ? 'Y' : 'N');

const FIELD_LABEL: Record<string, string> = {
  phone: 'Phone', contact: 'Contact', fax: 'Fax', other: 'Other',
  email: 'Email', website: 'Website', instructions: 'Instructions',
};

interface ContactGroup {
  key: string;
  label: string;
  fields: string[];
  toggle?: { field: string; label: string };
}

const CONTACT_GROUPS: ContactGroup[] = [
  { key: 'imp_custserv', label: 'Import Customer Service', fields: ['phone', 'contact', 'fax', 'other', 'email'] },
  { key: 'exp_custserv', label: 'Export Customer Service', fields: ['phone', 'contact', 'fax', 'other', 'email'] },
  { key: 'ces_custserv', label: 'CES Customer Service', fields: ['phone', 'contact', 'fax', 'other', 'email'] },
  { key: 'equip', label: 'Equipment', fields: ['phone', 'contact', 'fax', 'other', 'email'] },
  { key: 'street', label: 'Street Turn', fields: ['phone', 'contact', 'fax', 'other', 'email', 'website', 'instructions'] },
  { key: 'imp_railbill', label: 'Import Rail Billing', toggle: { field: 'importrailbilling', label: "Import Railbilling Req'd" }, fields: ['phone', 'contact', 'fax', 'other', 'email', 'website', 'instructions'] },
  { key: 'exp_railbill', label: 'Export Rail Billing', toggle: { field: 'exportrailbilling', label: "Export Railbilling Req'd" }, fields: ['phone', 'contact', 'fax', 'other', 'email', 'website', 'instructions'] },
  { key: 'chassisrel', label: 'Chassis Release', toggle: { field: 'chassisrelease', label: 'Chassis Release Required' }, fields: ['phone', 'contact', 'fax', 'other', 'email'] },
  { key: 'lfd', label: 'Last Free Day', fields: ['phone', 'contact', 'email', 'website', 'instructions'] },
  { key: 'offhire', label: 'Off-Hire', fields: ['phone', 'contact', 'email', 'website', 'instructions'] },
];

const GROUP_FIELD_KEYS = CONTACT_GROUPS.flatMap((g) => g.fields.map((f) => `${g.key}_${f}`));
// Per-section contact emails: validated (when non-blank) on save.
const EMAIL_FIELDS = CONTACT_GROUPS
  .filter((g) => g.fields.includes('email'))
  .map((g) => ({ key: `${g.key}_email`, label: `${g.label} Email` }));
const YN_FIELDS = ['importrailbilling', 'exportrailbilling', 'chassisrelease'];
const TEXT_KEYS = [...GROUP_FIELD_KEYS, 'chassisused', 'website', 'notes'];

const ScacInfoEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  const recno = row ? Number(row.recno) : 0;

  const [record, setRecord] = useState<TablemaintRow>(row ?? {});
  const [scacOpts, setScacOpts] = useState<ScacOption[]>([]);
  const [regionOpts, setRegionOpts] = useState<string[]>([]);
  const [form, setForm] = useState<TablemaintRow>(() => {
    const f: TablemaintRow = { scac: '', location: '' };
    YN_FIELDS.forEach((k) => { f[k] = 'N'; });
    TEXT_KEYS.forEach((k) => { f[k] = ''; });
    return f;
  });
  const [formDeleted, setFormDeleted] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [locMaps, setLocMaps] = useState<ScacLocationMapRow[]>([]);
  const [locMapEditing, setLocMapEditing] = useState<number | 'new' | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  const refreshLocMaps = useCallback((scac: string, location: string) => {
    if (!scac) { setLocMaps([]); return; }
    getScacLocationMap(scac, location).then(setLocMaps).catch(() => setLocMaps([]));
  }, []);

  useEffect(() => {
    getScacs().then(setScacOpts).catch(() => {});
    getRegions().then(setRegionOpts).catch(() => {});
    if (!isNew) tablemaintService.get(TABLE, recno).then(setRecord).catch(() => {});
  }, [isNew, recno]);

  useEffect(() => {
    if (isNew) return;
    const f: TablemaintRow = { scac: s(record.scac), location: s(record.location) };
    YN_FIELDS.forEach((k) => { f[k] = yn(record[k]); });
    TEXT_KEYS.forEach((k) => { f[k] = s(record[k]); });
    setForm(f);
    setFormDeleted(s(record.deleted) === 'Y');
    refreshLocMaps(s(record.scac), s(record.location));
  }, [record, isNew, refreshLocMaps]);

  useEffect(() => { if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [saveError, showErrors]);

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const scacSelectOpts = useMemo(() => scacOpts, [scacOpts]);
  const regionSelectOpts = useMemo(() => normalizeOptions(regionOpts), [regionOpts]);

  const errors = useMemo(() => {
    const e: string[] = [];
    if (!s(form.scac).trim()) e.push('SCAC is required.');
    EMAIL_FIELDS.forEach(({ key, label }) => {
      const bad = findInvalidEmails(s(form[key]), { semicolon: true });
      if (bad.length) e.push(`Invalid ${label}: ${bad.join(', ')}`);
    });
    return e;
  }, [form]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    const payload: TablemaintRow = { scac: s(form.scac).trim(), location: s(form.location).trim() };
    YN_FIELDS.forEach((k) => { payload[k] = yn(form[k]); });
    TEXT_KEYS.forEach((k) => { payload[k] = s(form[k]); });
    try {
      if (isNew) {
        await tablemaintService.create(TABLE, payload);
      } else {
        const patch: TablemaintRow = {};
        [...YN_FIELDS, ...TEXT_KEYS].forEach((k) => {
          if (s(payload[k]) !== s(record[k])) patch[k] = payload[k];
        });
        if (formDeleted !== (s(record.deleted) === 'Y')) patch.deleted = formDeleted ? 'Y' : 'N';
        if (Object.keys(patch).length === 0) { onClose(); return; }
        await tablemaintService.update(TABLE, recno, patch);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save SCAC Info.');
    } finally {
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, isNew, form, record, formDeleted, recno, onClose, onSaved]);

  // ── Contact-group rendering ─────────────────────────────────────────────
  const renderGroupField = (groupKey: string, field: string) => {
    const key = `${groupKey}_${field}`;
    const label = FIELD_LABEL[field];
    if (field === 'instructions') {
      return (
        <div key={key} className="md:col-span-2">
          <TextAreaField label={label} value={s(form[key])} onChange={(v) => setField(key, v)} disabled={isSaving} />
        </div>
      );
    }
    const full = field === 'email' || field === 'website';
    return (
      <div key={key} className={full ? 'md:col-span-2' : undefined}>
        <TextField label={label} value={s(form[key])} onChange={(v) => setField(key, v)} disabled={isSaving} />
      </div>
    );
  };

  const renderGroup = (g: ContactGroup) => (
    <fieldset key={g.key} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <legend className="px-2 text-sm font-semibold text-label">{g.label}</legend>
      {g.toggle && (
        <div className="mb-3">
          <Toggle label={g.toggle.label} checked={s(form[g.toggle.field]) === 'Y'} disabled={isSaving}
            onChange={(v) => setField(g.toggle!.field, v ? 'Y' : 'N')} />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {g.fields.map((f) => renderGroupField(g.key, f))}
      </div>
    </fieldset>
  );

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {isNew ? 'Create SCAC Info' : 'Save Changes'}</>)}
      </button>
    </div>
  );

  const title = isNew ? 'Add SCAC Info' : `Edit SCAC Info (#${recno})`;

  return (
    <>
      <Drawer isOpen onClose={onClose} title={title} position="right" width="60rem" footer={footer}>
        <div className="space-y-5">
          <div ref={topRef} />
          {!isNew && (
            <div className="flex justify-end">
              <EditHistoryLink table={TABLE} recno={recno} />
            </div>
          )}
          {banner.length > 0 && (
            <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
              <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
                {banner.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}

          {/* Identity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isNew ? (
              <SelectField label="SCAC" required value={s(form.scac)} onChange={(v) => setField('scac', v)} options={scacSelectOpts} disabled={isSaving} />
            ) : (
              <div>
                <label className="block text-sm font-medium text-label mb-1">SCAC</label>
                <div className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-body">{s(form.scac) || '—'}</div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-label mb-1">SS Line</label>
              <div className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-body">{s(record.ssline) || '—'}</div>
            </div>
            {isNew ? (
              <SelectField label="Location" value={s(form.location)} onChange={(v) => setField('location', v)} options={regionSelectOpts} disabled={isSaving} />
            ) : (
              <div>
                <label className="block text-sm font-medium text-label mb-1">Location</label>
                <div className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-body">{s(form.location) || '—'}</div>
              </div>
            )}
            <div className="md:col-span-2">
              <TextField label="Website" value={s(form.website)} onChange={(v) => setField('website', v)} disabled={isSaving} maxLength={255} />
            </div>
          </div>

          {/* Rail Port / CY Locations subtable */}
          <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <legend className="px-2 text-sm font-semibold text-label">Rail Port / CY Locations</legend>
            {isNew ? (
              <p className="text-sm text-muted">Save this SCAC Info record first to add rail port / CY locations.</p>
            ) : (
              <>
                {locMaps.length === 0 && (
                  <p className="text-sm text-muted mb-2">No rail port / CY locations mapped yet.</p>
                )}
                <div className="space-y-1">
                  {locMaps.map((m) => (
                    <button key={m.recno} type="button" onClick={() => setLocMapEditing(m.recno)}
                      className="w-full text-left rounded-md border border-gray-200 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-800 dark:text-gray-200 hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/20">
                      <span className="font-medium">{m.rp_lname || '—'}</span>
                      <span className="text-gray-400"> / </span>
                      <span className="font-medium">{m.cy_lname || '—'}</span>
                      {m.other && <span className="text-muted"> · {m.other}</span>}
                    </button>
                  ))}
                </div>
                <button type="button" onClick={() => setLocMapEditing('new')} disabled={isSaving}
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-50">
                  <PlusIcon className="w-4 h-4" /> Add Location
                </button>
              </>
            )}
          </fieldset>

          {/* Contact & service details — one collapsible block */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <button type="button" onClick={() => setDetailsOpen((o) => !o)}
              aria-expanded={detailsOpen}
              className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50">
              <span className="text-sm font-semibold text-label">Contact &amp; Service Details</span>
              <span className="flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400">
                {detailsOpen ? 'Hide' : 'Show'}
                <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 transition-transform ${detailsOpen ? 'rotate-180' : ''}`} />
              </span>
            </button>
            {detailsOpen && (
              <div className="border-t border-gray-200 dark:border-gray-700 p-3 space-y-3">
                {CONTACT_GROUPS.map((g) => (
                  <React.Fragment key={g.key}>
                    {renderGroup(g)}
                    {g.key === 'chassisrel' && (
                      <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <legend className="px-2 text-sm font-semibold text-label">Chassis Used</legend>
                        <TextField label="Chassis Used" value={s(form.chassisused)} onChange={(v) => setField('chassisused', v)} disabled={isSaving} maxLength={255} />
                      </fieldset>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <TextAreaField label="Notes" value={s(form.notes)} onChange={(v) => setField('notes', v)} disabled={isSaving} />

          {!isNew && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <Toggle label="Deleted" checked={formDeleted} disabled={isSaving} onChange={setFormDeleted}
                hint="Soft-deletes this SCAC Info record — hidden from filters but preserved in history. Uncheck and save to restore." />
            </div>
          )}
        </div>
      </Drawer>

      {/* Nested SCAC Location drawer (scac + location locked to this record) */}
      {locMapEditing !== null && (
        <ScacLocationMapEditDrawer
          row={locMapEditing === 'new' ? null : { recno: locMapEditing }}
          lockedScac={s(form.scac)}
          lockedLocation={s(form.location)}
          onClose={() => setLocMapEditing(null)}
          onSaved={() => refreshLocMaps(s(form.scac), s(form.location))}
        />
      )}
    </>
  );
};

export default ScacInfoEditDrawer;
